import { useState } from 'react';
import { Comment } from './PlantComments';
import { useNavigate } from 'react-router-dom';

interface RecentExperiencesProps {
  comments: Comment[];
  loading: boolean;
  plantId: string;
}

export const RecentExperiences = ({ comments, loading, plantId }: RecentExperiencesProps) => {
  const navigate = useNavigate();
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const latestComments = comments.slice(0, 3);

  const handleCommentClick = (comment: Comment) => {
    setSelectedComment(comment);
  };

  const closeModal = () => {
    setSelectedComment(null);
  };

  if (loading) {
    return (
      <div className="mt-6 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-gray-100 p-3 rounded-lg">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="mt-4 text-sm text-gray-500">
        No experiences shared yet. Be the first to share yours!
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">Recent Experiences</h3>
        {comments.length > 3 && (
          <button
            onClick={() => navigate(`/plants/${plantId}/experiences`)}
            className="text-xs text-green-600 hover:text-green-800 hover:underline"
          >
            View All
          </button>
        )}
      </div>
      
      <div className="space-y-3">
        {latestComments.map((comment) => (
          <div 
            key={comment._id} 
            className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow transition-shadow cursor-pointer"
            onClick={() => handleCommentClick(comment)}
          >
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium text-gray-900">{comment.name}</h4>
              {comment.ailment && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                  {comment.ailment}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-600 line-clamp-2">
              {comment.experience}
            </p>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {comment.preparationMethod}
              </span>
              <span className="text-xs text-gray-400">
                {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Comment Detail Modal */}
      {selectedComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{selectedComment.name}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedComment.createdAt ? new Date(selectedComment.createdAt).toLocaleDateString() : ''}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                {selectedComment.ailment && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Ailment/Condition</h4>
                    <p className="mt-1 text-gray-900">{selectedComment.ailment}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Experience</h4>
                  <p className="mt-1 text-gray-900 whitespace-pre-line">{selectedComment.experience}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Preparation Method</h4>
                    <p className="mt-1 text-gray-900">{selectedComment.preparationMethod || 'Not specified'}</p>
                  </div>
                  
                  {selectedComment.dosage && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Dosage</h4>
                      <p className="mt-1 text-gray-900">{selectedComment.dosage}</p>
                    </div>
                  )}
                </div>
                
                {selectedComment.sideEffects && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Side Effects</h4>
                    <p className="mt-1 text-gray-900">{selectedComment.sideEffects}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    closeModal();
                    navigate(`/plants/${plantId}/experiences`);
                  }}
                  className="text-sm font-medium text-green-600 hover:text-green-800"
                >
                  View all experiences →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentExperiences;
